import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppSkinProps } from '../../../types/decisions';
import AppResultsV2 from '../app-components/AppResultsV2/AppResultsV2';
import CriteriaImportanceAndSettingsV2 from '../app-components/CriteriaImportanceAndSettingsV2';
import Loading from '../../loading/Loading';
import { useAppConfig } from '../../../context/app-configs-context';
import AppInfo from '../app-components/AppInfo';
import { getCriteriaFieldsToDisplayInCriteria, getCriteriaFieldsToDisplayInResults } from '../../../utils';
import { usePostMessage } from '../../../hooks/usePostMessage';

type AgentMentionData = {
    appId?: string,
    source: 'maria_agent',
    payload: {
                 op: 'add',
                 fieldIds: string[],
                 options?: { fieldId: string, values: string[] }[],
                 channel?: 'voice' | 'text',
             }
             | {
                 op: 'clear'
             },
};

type AgentSentUpdateData = {
    appId?: string,
    source: 'maria_agent',
    payload: {
        focus: string[],
        changes: {
            target: 'criterion',
            fieldId: string,
            patch?: {
                value?: Record<string, string | number | boolean> | string | number | boolean,
                importance?: number,
            },
        }[],
        rerank: boolean,
    },
};

type HostChange = {
    target: 'criterion',
    fieldId: string,
    property: string,
    newValue: unknown,
};
type HostStateData = {
    source: 'system',
    appId: string,
    payload: {
        appState: AppSkinProps['criteriaData'],
        appName: string,
        appDescription: string,
        changes?: HostChange[],
        rank: AppSkinProps['results'],
    },
};

const agentChatIframeSettings: {
    id: string;
    url: string;
    title: string;
    collapsedHeight: number;
    expandedHeight: number;
    collapsedWidth: number;
    expandedWidth: number;
    styles: React.CSSProperties;
} = {
    id: 'maria-chat',
    url: 'https://agenticonvo.com/maria-agent/chat.html',
    title: 'Maria conversation',
    collapsedHeight: 66,
    expandedHeight: 480,
    collapsedWidth: 66,
    expandedWidth: 360,
    styles: {
        borderRadius: 14,
        position: 'fixed',
        right: 12,
        bottom: 78,
        zIndex: 9997,
    },
};

const MARIA_EMBED_URL = 'https://agenticonvo.com/maria-agent/embed.html';
const AGENT_IFRAME_ID = 'maria-v3';
const AGENT_APPLY_DELAY = 500;
const HIGHLIGHT_RESET_DELAY = 1_000;
const HIGHLIGHT_STAGGER_DELAY = 250;
const APP_EVENTS = {
    ON_AGENT_READY: 'MARIA_AGENT_READY',
    ON_AGENT_SENT_UPDATE: 'UPDATE',
    ON_APP_LOAD: 'ON_LOAD',
    ON_APP_UPDATE: 'ON_UPDATE',
    ON_AGENT_HIGHLIGHTS: 'MARIA_AGENT_MENTION',
    ON_AGENT_CHAT_OPEN: 'MARIA_AGENT_CHAT_OPEN',
    ON_AGENT_CHAT_CLOSE: 'MARIA_AGENT_CHAT_CLOSE',
};

const AppSkin9 = ({
    results,
    onRerank,
    onRerankMany,
    criteriaLoading,
    criteriaData,
    criteriaConstraints,
    onSliderChange,
    onResetTradeOffs,
    onCriteriaApply,
    onNotifyApply,
    resultLoading,
    pageInfo,
    onPageChange,
    onTradeoffValueChange,
    fieldInitialValues,
    selectedProductIds,
    setSelectedProductIds,
    renderCustomSettings,
}: AppSkinProps) => {
    const { appConfigs } = useAppConfig();
    const shouldShowRankByManyCheckbox = appConfigs.inferenceRank === 'ThumbsUp';
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const [ isAgentReady, setIsAgentReady ] = useState(false);
    const hasSentLoadRef = useRef(false);
    const agentApplyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [ highlightedFieldIds, setHighlightedFieldIds ] = useState<Set<string>>(new Set());
    const [ highlightedOptions, setHighlightedOptions ] = useState<Record<string, string[]>>({});
    const [ revealedOptions, setRevealedOptions ] = useState<Record<string, string[]>>({});
    const [ changedOptions, setChangedOptions ] = useState<Record<string, Record<string, {
        from: number; to: number
    }>>>({});
    const fieldHighlightTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const fieldHighlightStaggerTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const [ agentFocusedFieldId, setAgentFocusedFieldId ] = useState<string | null>(null);
    const [ isChatOpen, setIsChatOpen ] = useState(false);

    const handleAgentReady = useCallback(() => {
        console.log('Received: Agent is Ready');
        setIsAgentReady(true);
    }, []);

    const buildHostStateData = useCallback((changes?: HostChange[]): HostStateData => ({
        source: 'system',
        appId: criteriaData.id,
        payload: {
            appState: criteriaData,
            appName: criteriaData.name,
            appDescription: criteriaData.description,
            ...(changes ? { changes } : {}),
            rank: results,
        },
    }), [ criteriaData, results ]);

    const handleAgentSentUpdate = useCallback((data: AgentSentUpdateData) => {
        console.log('Received: Agent Sent Update', data);
        const changes = data?.payload?.changes ?? [];

        changes.forEach(({ fieldId, patch }) => {
            if (!fieldId || !patch) {
                return;
            }

            if (typeof patch.importance === 'number') {
                onSliderChange(patch.importance, fieldId);
            }

            if (patch.value !== undefined && patch.value !== null) {
                if (typeof patch.value === 'object') {
                    const entries = Object.entries(patch.value);
                    const currentField = criteriaData.fields.find(item => item.id === fieldId);

                    setRevealedOptions(prev => {
                        const next = { ...prev };
                        const merged = new Set([ ...(next[fieldId] ?? []), ...entries.map(([ key ]) => key) ]);

                        next[fieldId] = Array.from(merged);

                        return next;
                    });

                    setChangedOptions(prev => ({
                        ...prev,
                        [fieldId]: {
                            ...prev[fieldId],
                            ...Object.fromEntries(entries.map(([ key, newValue ]) => {
                                const rawFrom = (currentField?.val as Record<string, unknown> | undefined)?.[key] as number;

                                return [ key, { from: Number(rawFrom ?? 0), to: Number(newValue) } ];
                            })),
                        },
                    }));

                    entries.forEach(([ key, newValue ]) => {
                        onTradeoffValueChange(newValue, fieldId, key);
                    });
                } else {
                    onTradeoffValueChange(patch.value, fieldId);
                }
            }
        });

        const focus = data?.payload?.focus;
        setAgentFocusedFieldId(prev => {
            if (Array.isArray(focus) && focus.length > 0) {
                return String(focus[focus.length - 1]);
            }

            const answeredIds = changes
            .filter(change => change.patch && 'value' in change.patch)
            .map(change => change.fieldId);

            return prev && answeredIds.includes(prev) ? null : prev;
        });

        if (data?.payload?.rerank) {
            if (agentApplyTimeoutRef.current) {
                clearTimeout(agentApplyTimeoutRef.current);
            }

            agentApplyTimeoutRef.current = setTimeout(() => {
                onCriteriaApply(dataset);
                agentApplyTimeoutRef.current = null;
            }, AGENT_APPLY_DELAY);
        }
    }, [ onSliderChange, onTradeoffValueChange, onCriteriaApply, dataset, criteriaData ]);

    const scheduleFieldHighlightReset = useCallback((id: string) => {
        const existing = fieldHighlightTimeoutsRef.current.get(id);

        if (existing) {
            clearTimeout(existing);
        }

        fieldHighlightTimeoutsRef.current.set(id, setTimeout(() => {
            fieldHighlightTimeoutsRef.current.delete(id);
            setHighlightedFieldIds(prev => {
                if (!prev.has(id)) {
                    return prev;
                }

                const next = new Set(prev);

                next.delete(id);

                return next;
            });
        }, HIGHLIGHT_RESET_DELAY));
    }, []);

    const handleAgentMentions = useCallback((data: AgentMentionData) => {
        if (data?.source !== 'maria_agent' || !data?.payload) {
            return;
        }

        if (data.payload.op === 'clear') {
            fieldHighlightTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            fieldHighlightTimeoutsRef.current.clear();
            fieldHighlightStaggerTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            fieldHighlightStaggerTimeoutsRef.current = [];
            setHighlightedFieldIds(new Set());
            setHighlightedOptions({});
            setRevealedOptions({});
            setChangedOptions({});
        } else if (data.payload.op === 'add') {
            if (Array.isArray(data.payload.fieldIds) && data.payload.fieldIds.length > 0) {
                const ids = data.payload.fieldIds.map(String);

                fieldHighlightStaggerTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
                fieldHighlightStaggerTimeoutsRef.current = [];

                ids.forEach((id, index) => {
                    const revealField = () => {
                        setHighlightedFieldIds(prev => {
                            if (prev.has(id)) {
                                return prev;
                            }

                            const next = new Set(prev);

                            next.add(id);

                            return next;
                        });

                        scheduleFieldHighlightReset(id);
                    };

                    if (index === 0) {
                        revealField();
                    } else {
                        fieldHighlightStaggerTimeoutsRef.current.push(
                            setTimeout(revealField, index * HIGHLIGHT_STAGGER_DELAY),
                        );
                    }
                });
            }

            if (Array.isArray(data.payload.options) && data.payload.options.length > 0) {
                const entries = data.payload.options;

                setHighlightedOptions(prev => {
                    const next = { ...prev };

                    entries.forEach(({ fieldId, values }) => {
                        if (!fieldId || !Array.isArray(values)) {
                            return;
                        }
                        const merged = new Set([ ...(next[fieldId] ?? []), ...values.map(String) ]);

                        next[fieldId] = Array.from(merged);
                    });

                    return next;
                });

                setRevealedOptions(prev => {
                    const next = { ...prev };

                    entries.forEach(({ fieldId, values }) => {
                        if (!fieldId || !Array.isArray(values)) {
                            return;
                        }
                        const merged = new Set([ ...(next[fieldId] ?? []), ...values.map(String) ]);

                        next[fieldId] = Array.from(merged);
                    });

                    return next;
                });
            }
        }
    }, [ scheduleFieldHighlightReset ]);

    useEffect(() => {
        const fieldHighlightTimeouts = fieldHighlightTimeoutsRef.current;
        const fieldHighlightStaggerTimeouts = fieldHighlightStaggerTimeoutsRef.current;

        return () => {
            if (agentApplyTimeoutRef.current) {
                clearTimeout(agentApplyTimeoutRef.current);
            }

            fieldHighlightTimeouts.forEach(timeout => clearTimeout(timeout));
            fieldHighlightStaggerTimeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    const handleAgentChatOpen = useCallback(() => {
        setIsChatOpen(true);
    }, []);

    const handleAgentChatClose = useCallback(() => {
        setIsChatOpen(false);
    }, []);

    usePostMessage(APP_EVENTS.ON_AGENT_READY, 'subscribe', handleAgentReady);
    usePostMessage<AgentSentUpdateData>(APP_EVENTS.ON_AGENT_SENT_UPDATE, 'subscribe', handleAgentSentUpdate);
    usePostMessage<AgentMentionData>(APP_EVENTS.ON_AGENT_HIGHLIGHTS, 'subscribe', handleAgentMentions);
    usePostMessage(APP_EVENTS.ON_AGENT_CHAT_OPEN, 'subscribe', handleAgentChatOpen);
    usePostMessage(APP_EVENTS.ON_AGENT_CHAT_CLOSE, 'subscribe', handleAgentChatClose);
    const onAppLoad = usePostMessage<HostStateData>(APP_EVENTS.ON_APP_LOAD, 'emit');
    const onUserUpdate = usePostMessage<HostStateData>(APP_EVENTS.ON_APP_UPDATE, 'emit');

    const chatChannelId = criteriaData.id;
    const agentEmbedSrc = useMemo(() => `${MARIA_EMBED_URL}?chat=external&ch=${chatChannelId}`, [ chatChannelId ]);
    const agentChatSrc = useMemo(() => `${agentChatIframeSettings.url}?ch=${chatChannelId}`, [ chatChannelId ]);

    const onSliderChangeWrapper: AppSkinProps['onSliderChange'] = (newValue, id) => {
        onSliderChange(newValue, id);
        onUserUpdate(AGENT_IFRAME_ID, buildHostStateData([
            {
                target: 'criterion',
                fieldId: id,
                property: 'importance',
                newValue,
            },
        ]));
    };

    const displayFieldsIds: string[] = useMemo(() => {
        return getCriteriaFieldsToDisplayInResults(criteriaData.fields).map(item => item.id);
    }, [ criteriaData.fields ]);

    useEffect(() => {
        if (!isAgentReady || hasSentLoadRef.current) {
            return;
        }

        if (criteriaLoading || !criteriaData?.fields?.length) {
            return;
        }

        hasSentLoadRef.current = true;
        onAppLoad(AGENT_IFRAME_ID, buildHostStateData());
    }, [ isAgentReady, criteriaLoading, criteriaData, buildHostStateData, onAppLoad ]);

    useEffect(() => {
        if (!hasSentLoadRef.current) {
            return;
        }

        onUserUpdate(AGENT_IFRAME_ID, buildHostStateData([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ results ]);

    return (
        <div className="application">
            {criteriaLoading ? (
                <Loading/>
            ) : (
                <>
                    <AppInfo
                        icon={criteriaData.icon}
                        name={criteriaData.name}
                        description={criteriaData.description}
                    />
                    <div className="application-details">
                        <div className="app-criteria" id="app-criteria" style={{ width: '28%' }}>
                            <div>
                                <iframe
                                    id={AGENT_IFRAME_ID}
                                    width="100%"
                                    height="285"
                                    src={agentEmbedSrc}
                                    allow="microphone; autoplay"
                                    title="Maria shopping assistant"
                                    style={{ border: 'none' }}
                                />
                            </div>
                            <CriteriaImportanceAndSettingsV2
                                shouldUseHeartSlider
                                criteriaFields={getCriteriaFieldsToDisplayInCriteria(criteriaData.fields)}
                                onSliderChange={onSliderChangeWrapper}
                                fieldInitialValues={fieldInitialValues}
                                onTradeoffValueChange={onTradeoffValueChange}
                                onResetTradeOffs={onResetTradeOffs}
                                onCriteriaApply={onCriteriaApply}
                                onNotifyApply={onNotifyApply}
                                externalItems={appConfigs.show.menuItems.externalItems}
                                renderCustomSettings={renderCustomSettings}
                                decisionConfig={criteriaData?.config}
                                appId={criteriaData?.id}
                                highlightedFieldIds={highlightedFieldIds}
                                highlightedOptions={highlightedOptions}
                                revealedOptionsByField={revealedOptions}
                                changedOptionsByField={changedOptions}
                                agentFocusedFieldId={agentFocusedFieldId}
                                modalClassName="criteria-modal-compact"
                                criteria={criteriaData}
                                constraints={criteriaConstraints}
                            />
                        </div>
                        <div className="app-results" style={{ width: '72%' }}>
                            {resultLoading ? (
                                <Loading/>
                            ) : (
                                <AppResultsV2
                                    results={results}
                                    pageInfo={pageInfo}
                                    onPageChange={onPageChange}
                                    onRerank={onRerank}
                                    onRerankMany={onRerankMany}
                                    shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                                    selectedProductIds={selectedProductIds}
                                    setSelectedProductIds={setSelectedProductIds}
                                    allowedFieldsIds={displayFieldsIds}
                                    criteriaData={criteriaData}
                                    criteriaConstraints={criteriaConstraints}
                                />
                            )}
                        </div>
                    </div>
                    <iframe
                        id={agentChatIframeSettings.url}
                        src={agentChatSrc}
                        title={agentChatIframeSettings.title}
                        style={{
                            ...agentChatIframeSettings.styles,
                            border: isChatOpen ? '1px solid #e4e7ec' : 'none',
                            height: isChatOpen ? agentChatIframeSettings.expandedHeight : agentChatIframeSettings.collapsedHeight,
                            width: isChatOpen ? agentChatIframeSettings.expandedWidth : agentChatIframeSettings.collapsedWidth,
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default AppSkin9;
